import { watch } from 'vue';

import {
  WIDGET_ATTACHMENT_MAX_BYTES,
  WIDGET_ATTACHMENT_MAX_LABEL,
  isWidgetAttachmentMimeType,
} from '@support/widget-contracts';

import { WidgetApiError, request } from '@/api/client';
import { attachmentsAvailable, isUnknownOperation } from '@/api/compat';
import { CREATE_WIDGET_UPLOAD, TICKET_ATTACHMENT } from '@/api/documents';
import { currentIdentity, withSession } from '@/api/session';

/**
 * Why a file cannot be attached, in words for the person who chose it — or
 * null when it can.
 *
 * A courtesy, not the rule: the API refuses the same file again, and its
 * upload link is signed for one exact size and type. Checking here means the
 * visitor hears the limit before waiting on an upload that could only fail.
 */
export const refusalFor = (file: File): string | null => {
  if (!isWidgetAttachmentMimeType(file.type)) {
    return `"${file.name}" is not a PNG, JPEG, GIF or WebP image.`;
  }

  if (file.size > WIDGET_ATTACHMENT_MAX_BYTES) {
    return `"${file.name}" is too large. The limit is ${WIDGET_ATTACHMENT_MAX_LABEL} per image.`;
  }

  if (file.size === 0) {
    return `"${file.name}" is empty.`;
  }

  return null;
};

/**
 * Store one image and return the id a message names it by.
 *
 * Two requests: the API reserves a place and signs a link for this file's
 * exact size and type, then the bytes go straight to storage — they never pass
 * through the API. `Content-Type` has to be the declared one; it is part of
 * what was signed.
 */
export const uploadAttachment = async (file: File): Promise<string> => {
  const data = await withSession((token) => request<{
    createWidgetUpload: { fileId: string; uploadUrl: string };
  }>(
    CREATE_WIDGET_UPLOAD,
    { input: { name: file.name, mimeType: file.type, size: file.size } },
    token,
  )).catch((error: unknown) => {
    // An API that predates attachments — see `compat.ts`. Said in words the
    // visitor can act on, and remembered, so the button stops offering it.
    if (isUnknownOperation(error)) {
      attachmentsAvailable.value = false;

      throw new WidgetApiError('Images cannot be attached right now. Send your message without them.');
    }

    throw error;
  });

  const failed = new WidgetApiError(`Could not upload "${file.name}". Check your connection and try again.`);
  let response: Response;

  try {
    response = await fetch(data.createWidgetUpload.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      credentials: 'omit',
      body: file,
    });
  } catch {
    throw failed;
  }

  if (!response.ok) {
    throw failed;
  }

  return data.createWidgetUpload.fileId;
};

interface CachedLink {
  url: string;
  staleAt: number;
}

/**
 * Links already asked for, by thread and file.
 *
 * A link is good for minutes and a thread is redrawn far more often than that
 * — every reopen, and every reply that re-reads it. Without this each redraw
 * would ask again for every image and spend the visitor's rate limit on files
 * that are already on screen. Held in memory only, like the session.
 */
const links = new Map<string, CachedLink>();

/** Asked again this long before a link stops answering. */
const STALE_MARGIN_MS = 60_000;

/** A link to one file of one of the visitor's own threads. */
export const fetchAttachmentUrl = async (ticketId: string, fileId: string): Promise<string> => {
  const askedFor = currentIdentity.value;
  const key = `${ticketId}\n${fileId}`;
  const cached = links.get(key);

  if (cached && cached.staleAt > Date.now()) {
    return cached.url;
  }

  const data = await withSession((token) => request<{
    ticketAttachment: { url: string; expiresInSeconds: number };
  }>(TICKET_ATTACHMENT, { ticketId, fileId }, token));

  // The frame opens and draws what it is handed, so it takes a web address
  // and nothing else — whatever the API meant to send.
  if (!/^https?:\/\//i.test(data.ticketAttachment.url)) {
    throw new WidgetApiError('This file is no longer available.');
  }

  // The answer is about whoever asked. If the host has signed somebody else in
  // since, it is neither kept nor handed back: the caller is a click or a
  // thumbnail of a panel that is gone, and what it would do with the link is
  // open one person's file in a frame that now belongs to another. Not caching
  // it is not enough — see `refreshUnread` for the same race on the launcher.
  if (askedFor !== currentIdentity.value) {
    throw new WidgetApiError('This file is no longer available.');
  }

  links.set(key, {
    url: data.ticketAttachment.url,
    staleAt: Date.now() + data.ticketAttachment.expiresInSeconds * 1_000 - STALE_MARGIN_MS,
  });

  return data.ticketAttachment.url;
};

// A link is a capability for whoever holds it: the next person signed in on
// this page must not inherit the last one's.
watch(currentIdentity, () => {
  links.clear();
});
